/**
 * 🏠 Secret Property Card - Advanced Edition
 * Full-featured property card with all Secret Deals functionality
 */

import React, { useState, useEffect } from 'react';
import HotDealBadge from './HotDealBadge';
import DealMatchScore from './DealMatchScore';
import IntentButtons from './IntentButtons';
import FirstLookBadge from './FirstLookBadge';
import RiskFlags from './RiskFlags';
import { 
  detectHotDeal, 
  calculateDealMatchScore, 
  checkFirstLookAccess,
  analyzePropertyRisk,
  calculateDealReadinessScore,
  getPrioritizedBuyers
} from '../../services/secretDealsService';

const SecretPropertyCard = ({ 
  property, 
  buyerId, 
  showMatchScore = false,
  showRisk = true,
  expanded = false 
}) => {
  const [deal, setDeal] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [firstLook, setFirstLook] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [readinessData, setReadinessData] = useState(null);
  const [prioritizedBuyers, setPrioritizedBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullDetails, setShowFullDetails] = useState(expanded);

  useEffect(() => {
    loadAllData();
  }, [property.id, buyerId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // 1. Detect HOT DEAL
      const hotDeal = await detectHotDeal(property, { averagePrice: property.marketPrice });
      setDeal(hotDeal);

      // 2. Calculate Match Score
      if (showMatchScore && buyerId) {
        const match = await calculateDealMatchScore(buyerId, property.id);
        setMatchData(match);
      }

      // 3. Check First Look Access
      const firstLookAccess = await checkFirstLookAccess(property.id, buyerId);
      setFirstLook(firstLookAccess);

      // 4. Analyze Risks
      if (showRisk) {
        const risk = await analyzePropertyRisk(property, { averagePrice: property.marketPrice });
        setRiskData(risk);
      }

      // 5. Deal Readiness (if seller data available)
      if (property.sellerData) {
        const readiness = await calculateDealReadinessScore(property.id, property.sellerData);
        setReadinessData(readiness);
      }

      // 6. Get Prioritized Buyers (for seller view)
      const buyers = await getPrioritizedBuyers(property.id, 5);
      setPrioritizedBuyers(buyers);

    } catch (error) {
      console.error('Error loading property data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    if (price >= 1000000) {
      return `${(price / 1000000).toFixed(1)}M Ft`;
    }
    return `${price.toLocaleString()} Ft`;
  };

  // Calculate discount
  const getDiscount = () => {
    if (property.marketPrice && property.price) {
      const discount = ((property.marketPrice - property.price) / property.marketPrice) * 100;
      return discount > 0 ? `-${discount.toFixed(0)}%` : null;
    }
    return null;
  };

  const discount = getDiscount();

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-2xl p-6 animate-pulse">
        <div className="h-48 bg-gray-700 rounded-xl mb-4"></div>
        <div className="h-6 bg-gray-700 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-700 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className={`
      relative bg-gray-900 rounded-2xl overflow-hidden
      border ${deal?.isHot ? 'border-red-500/50' : 'border-white/10'}
      ${deal?.isHot ? 'shadow-lg shadow-red-500/20' : ''}
      transition-all duration-300 hover:border-white/[0.08]
    `}>
      {/* HOT Status Bar */}
      {deal?.isHot && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 animate-pulse z-10"></div>
      )}

      {/* Image Section */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={property.images?.[0] || '/placeholder-property.jpg'} 
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
        
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        
        {/* Top Badges */}
        <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-2">
          {/* HOT DEAL Badge */}
          {deal?.isHot && (
            <HotDealBadge deal={deal} size="small" />
          )}
          
          {/* First Look Badge */}
          {firstLook && (
            <FirstLookBadge 
              isActive={property.firstLookActive}
              expiresAt={property.firstLookExpiresAt}
              buyerCount={property.firstLookBuyers?.length}
              isPublic={firstLook.isPublic}
              hasAccess={firstLook.hasAccess}
            />
          )}
          
          {/* Off-Market Certified */}
          {property.offMarketCertified && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold">
              <span>🔒</span>
              <span>OFF-MARKET</span>
            </div>
          )}
        </div>

        {/* Discount Badge */}
        {discount && (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
            {discount} Alulárazott
          </div>
        )}

        {/* Match Score Overlay (if high) */}
        {showMatchScore && matchData?.score >= 70 && (
          <div className="absolute bottom-3 right-3">
            <DealMatchScore 
              score={matchData.score} 
              strength={matchData.strength}
              showDetails={false}
            />
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5">
        {/* Title & Location */}
        <div className="mb-3">
          <h3 className="text-xl font-bold text-white mb-1 line-clamp-1">
            {property.title || property.type}
          </h3>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span>📍</span>
            <span>{property.location}</span>
          </div>
        </div>

        {/* Price & Details */}
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-3xl font-black text-white">
              {formatPrice(property.price)}
            </div>
            {property.marketPrice && (
              <div className="text-sm text-gray-400">
                Piaci ár: <span className="line-through">{formatPrice(property.marketPrice)}</span>
              </div>
            )}
            {property.roiPotential && (
              <div className="text-sm text-green-400 font-semibold mt-1">
                💎 {property.roiPotential}% ROI potenciál
              </div>
            )}
          </div>
          
          {/* Property Details */}
          <div className="flex gap-3 text-sm text-gray-400">
            {property.area_m2 && (
              <div className="flex items-center gap-1">
                <span>📐</span>
                <span>{property.area_m2} m²</span>
              </div>
            )}
            {property.rooms && (
              <div className="flex items-center gap-1">
                <span>🚪</span>
                <span>{property.rooms} szoba</span>
              </div>
            )}
          </div>
        </div>

        {/* Match Score (if enabled and lower) */}
        {showMatchScore && matchData?.score < 70 && (
          <div className="mb-4 p-3 bg-gray-800/50 rounded-xl">
            <DealMatchScore 
              score={matchData.score} 
              reasons={matchData.reasons}
              strength={matchData.strength}
              showDetails={true}
            />
          </div>
        )}

        {/* Risk Analysis */}
        {showRisk && riskData && riskData.riskLevel !== 'low' && (
          <div className="mb-4">
            <RiskFlags analysis={riskData} compact={true} />
          </div>
        )}

        {/* Deal Readiness (for internal view) */}
        {readinessData && showFullDetails && (
          <div className="mb-4 p-3 bg-gray-800/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Eladó készültség</span>
              <span className={`text-sm font-bold ${
                readinessData.totalScore >= 70 ? 'text-green-400' :
                readinessData.totalScore >= 50 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {readinessData.grade.text} ({readinessData.totalScore}p)
              </span>
            </div>
            <p className="text-xs text-gray-500">{readinessData.recommendation}</p>
          </div>
        )}

        {/* Prioritized Buyers (for seller view) */}
        {prioritizedBuyers.length > 0 && showFullDetails && (
          <div className="mb-4">
            <div className="text-sm text-gray-400 mb-2">
              🎯 Top érdeklődők ({prioritizedBuyers.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {prioritizedBuyers.slice(0, 3).map((buyer, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-800 text-xs"
                >
                  <span>{buyer.intentIcon || '👤'}</span>
                  <span className="text-gray-300">{buyer.intentLabel}</span>
                  <span className={`
                    w-2 h-2 rounded-full
                    ${buyer.priority?.color === 'red' ? 'bg-red-500' :
                      buyer.priority?.color === 'orange' ? 'bg-orange-500' :
                      buyer.priority?.color === 'green' ? 'bg-green-500' : 'bg-gray-500'}
                  `}></span>
                </div>
              ))}
              {prioritizedBuyers.length > 3 && (
                <div className="px-2.5 py-1 rounded-full bg-gray-800 text-xs text-gray-400">
                  +{prioritizedBuyers.length - 3}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Intent Buttons */}
        {buyerId && (
          <div className="mb-4">
            <IntentButtons 
              propertyId={property.id}
              buyerId={buyerId}
              compact={true}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button 
            onClick={() => setShowFullDetails(!showFullDetails)}
            className="flex-1 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors text-sm"
          >
            {showFullDetails ? 'Kevesebb' : 'Részletek'}
          </button>
          
          <button className={`
            flex-1 px-4 py-3 rounded-xl font-bold text-sm transition-all
            ${deal?.isHot 
              ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white shadow-lg shadow-red-500/30 animate-pulse' 
              : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'}
          `}>
            {deal?.isHot ? '🔥 AZONNALI KAPCSOLAT' : 'Kapcsolatfelvétel'}
          </button>
        </div>

        {/* Urgency Message */}
        {deal?.isHot && deal.urgencyText && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-sm text-red-400 text-center font-semibold">
              ⚠️ {deal.urgencyText}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecretPropertyCard;
